// src/api/HATVCasesAPI.ts
import { createApi } from '@reduxjs/toolkit/query/react';
import axiosBaseQuery from './axiosBaseQuery/axiosBaseQuery';
import config from '../../config/config';

export interface HATVAwardApprovalABO {
  affiliateCode: string;
  aboNumber: string;
  name: string;
  localName: string;
  marketCountryCode: string;
  marketName: string;
  qMonth: number;
  qLeg: number;
  awardApproval: {
    aboHatvAwardId: number;
    finalApprovalNativeId: string;
    finalApprovalStatus: number;
    finalApprovalDate: string;
    awardApprovers: {
      userNativeId: string;
      delegateUserNativeIds: string[];
      approvalSequence: number;
      approvalNativeId: string;
      approvalStatus: number;
    }[];
  };
  accountManager: {
    userNativeId: string;
    primaryAccountManager: boolean;
  };
  hatvAdmin: {
    userNativeId: string;
    primaryHatvAdmin: true;
    delegateUserNativeIds: string[];
    secondaryUserNativeIds: string[];
  };
  currentAward: {
    awardCode: string;
    awardName: string;
  };
  trackingAward: {
    awardCode: string;
    awardName: string;
    awardTagCode: 1;
    awardTagName: string;
    awardQualifiedMonth: string;
    awardQualifiedYear: string;
  };
  uplineDiamond: {
    affiliateCode: string;
    aboNumber: string;
    name: string;
    localName: string;
    marketCountryCode: string;
    marketName: string;
  };
  uplinePlatinum: {
    affiliateCode: string;
    aboNumber: string;
    name: string;
    localName: string;
    marketCountryCode: string;
    marketName: string;
  };
  hatvApprovalRoleIndicators: {
    aboHatvAwardApproverIdToApprove: number | null;
    aboHatvAwardApproverIdToDeny: number | null;
    isAuthorizedToApprove: boolean;
    isAuthorizedToDeny: boolean;
    isAllowToApprove: boolean;
    isAllowToDeny: boolean;
  };
  hatvRoutingRoleIndicators: {
    isAuthorizedCreateRouting: boolean;
    isAllowCreateRouting: boolean;
    isAuthorizedUpdateResetRouting: boolean;
    isAllowUpdateResetRouting: boolean;
    isAuthorizedViewRouting: boolean;
  };
}

export interface HATVAwardApprovalResponse {
  closingMonth: string;
  abos: HATVAwardApprovalABO[];
}
export interface HATVAwardRoutingDetails {
  affiliateCode: string;
  aboNumber: string;
  name: string;
  localName: string;
  accountManager: {
    userNativeId: string;
    primaryAccountManager: boolean;
  };
  hatvAdmin: {
    userNativeId: string;
    primaryHatvAdmin: boolean;
    secondaryUserNativeIds: string[];
    delegateUserNativeIds: string[];
  };
  award: {
    aboHatvAwardId: number;
    performanceYear: number;
    awardCode: string;
    awardName: string;
    awardTagCode: string;
    awardTagName: string;
    finalApprovalNativeId: string;
    finalApprovalStatus: number;
    finalApprovalDate: string;
    status: number;
    awardApprovers: AwardApprover[] | null;
  };
}

export interface AwardApprover {
  aboHatvAwardApproverId: number;
  approvalSequence: number;
  userNativeId: string;
  approvalNativeId: string;
  approvalStatus: number;
  approvalDate: string;
  status: number;
  note: string;
}

export interface UserItem {
  userId: number;
  uid: string;
  fullName: string;
  nativeId: string;
  email: string;
  countryCode: string;
  affiliateCode: string;
  uplineNativeId: string;
  status: 1;
  teamId: string;
}

interface CreateRoutingMutationArgType {
  payload: {
    performanceYear: number;
    awards: {
      affiliateCode: string;
      aboNumber: string;
      awardCode: string;
      awardTagCode: string;
      awardQualifiedMonth: string;
    }[];
    awardApprovers: {
      approvalSequence: number;
      userNativeId: string;
    }[];
  };
}

interface ResetRoutingMutationArgType {
  affiliateCode: string;
  aboNumber: string;
  payload: {
    aboHatvAwardId: number;
    performanceYear: number;
    awardCode: string;
    awardQualifiedMonth: string;
    awardQualifiedYear: string;
    awardApprovers: {
      approvalSequence: number;
      userNativeId: string;
    }[];
  };
}


interface UpdateApprovalStatusArgType {
  affiliateCode: string;
  aboNumber: string;
  payload: {
    aboHatvAwardApproverId: number;
    approvalStatus: number;
    note: string;
  };
}

interface GetMyQMonthApprovalsRequest {
  closingMonth: string;
  payload?: {
    viewId: string | null;
    viewSetting: {
      qMonthApprovalAccountGroupCodes: number[];
      qMonthApprovalViolationGroupCodes: number[];
      qMonthApprovalStatusGroupCodes: number[];
      qMonthApprovalViolationTypeCodes: number[];
    } | null;
  };
}

interface HATVQMonthApprovalABO {
  affiliateCode: string;
  aboNumber: string;
  name: string;
  localName: string;
  marketCountryCode: string;
  marketName: string;
  qMonthApproval: {
    approvalNativeId: string;
    approvalStatus: number;
    approvalDate: string;
  };
  currentAward: {
    awardCode: string;
    awardName: string;
  };
  trackingAward: {
    awardCode: string;
    awardName: string;
    awardTagCode: number;
    awardTagName: string;
    awardQualifiedMonth: string;
    awardQualifiedYear: string;
  } | null;
  uplineDiamond: {
    affiliateCode: string;
    aboNumber: string;
    name: string;
    localName: string;
    marketCountryCode: string;
    marketName: string;
  };
  uplinePlatinum: {
    affiliateCode: string;
    aboNumber: string;
    name: string;
    localName: string;
    marketCountryCode: string;
    marketName: string;
  };
  accountManager: {
    userNativeId: string;
    primaryAccountManager: boolean;
  };
  hatvAdmin: {
    userNativeId: string;
    primaryHatvAdmin: true;
    delegateUserNativeIds: string[];
    secondaryUserNativeIds: string[];
  };
  hatvKpis: Record<
    string,
    {
      kpiValue: number;
      kpiFlagCode: number;
      kpiFlagName: 'RED' | 'YELLOW' | 'GREEN';
    }
  >;
  hatvApprovalRoleIndicators: {
    isAuthorizedToUpdate: boolean;
    isAllowToUpdate: boolean;
  };
}

export interface HATVQMonthAwardApprovalResponse {
  closingMonth: string;
  abos: HATVQMonthApprovalABO[];
}

export const HATVAwardApprovalApi = createApi({
  reducerPath: 'HATVAwardRouting',
  baseQuery: axiosBaseQuery({
    baseUrl: config?.appUrl,
  }),
  tagTypes: ['MyQMonthApprovals', 'MyApprovals', 'MyApprovalsDetails'],
  endpoints: (builder) => ({
    getMyQMonthApprovals: builder.query<
      HATVQMonthAwardApprovalResponse,
      GetMyQMonthApprovalsRequest
    >({
      query: ({ closingMonth, payload }) => ({
        url: `/am/myqmonthapproval/view/month/${closingMonth}`,
        method: 'POST',
        data: payload,
      }),
      providesTags: ['MyQMonthApprovals'],
      transformResponse: (res: {
        body: HATVQMonthAwardApprovalResponse;
        statusCode: number;
        error: {
          errorCode: number;
          errorMessage: string;
        }[];
      }) => {
        if (res.statusCode === 200) return res.body;
        throw res.error[0];
      },
    }),
    getMyAwardApproval: builder.query<
      HATVAwardApprovalResponse,
      {
        closingMonth: string;
      }
    >({
      query: ({ closingMonth }) => ({
        url: `/am/myawardapproval/month/${closingMonth}`,
        method: 'GET',
      }),
      providesTags: ['MyApprovals'],
      transformResponse: (res: {
        body: HATVAwardApprovalResponse;
        statusCode: number;
        error: {
          errorCode: number;
          errorMessage: string;
        }[];
      }) => {
        if (res.statusCode === 200) return res.body;
        throw res.error[0];
      },
    }),
    getAwardRoutingDetails: builder.query<
      HATVAwardRoutingDetails,
      {
        affiliateCode: string;
        aboNumber: string;
        performanceYear: string;
        awardCode: string;
        awardQualifiedMonth?: string;
      }
    >({
      query: ({ affiliateCode, aboNumber, performanceYear, awardCode, awardQualifiedMonth }) => ({
        url: `/am/abo/${affiliateCode}/${aboNumber}/awardapproval/routing/year/${performanceYear}/award/${awardCode}/qualifiedmonth/${awardQualifiedMonth}`,
        method: 'GET',
      }),
      providesTags: ['MyApprovalsDetails'],
      transformResponse: (res: {
        body: HATVAwardRoutingDetails;
        statusCode: number;
        error: {
          errorCode: number;
          errorMessage: string;
        }[];
      }) => {
        if (res.statusCode === 200) {
          if (!res.body.aboNumber) throw new Error('Data not available!');
          return res.body;
        }
        throw res.error[0];
      },
    }),
    getUsersList: builder.query<{ body: UserItem[] }, { affiliateCode: string }>({
      query: ({ affiliateCode }) => ({
        url: `/auth/users/active/application/AMW/permissiongrouptype/4/affiliate/${affiliateCode}/hatv`,
        method: 'GET',
      }),
    }),
    getUsersListByNativeIDs: builder.query<{ body: UserItem[] }, getUsersByNativeIDArgTypes>({
      query: ({ nativeIDs }) => ({
        url: `/auth/users/native`,
        method: 'GET',
        params: { native: nativeIDs },
      }),
    }),
    createAwardRouting: builder.mutation<any, CreateRoutingMutationArgType>({
      query: ({ payload }) => ({
        url: `/am/abo/awardapproval/routing/bulk/create`,
        method: 'POST',
        data: payload,
      }),
      transformResponse: (res: {
        body: any;
        statusCode: number;
        error: {
          errorCode: number;
          errorMessage: string;
        }[];
      }) => {
        if ([200, 201].includes(res.statusCode)) return res.body;
        throw res.error[0];
      },
      invalidatesTags: ['MyApprovals', 'MyApprovalsDetails'],
    }),
    resetAwardRouting: builder.mutation<any, ResetRoutingMutationArgType>({
      query: ({ affiliateCode, aboNumber, payload }) => ({
        url: `/am/abo/${affiliateCode}/${aboNumber}/awardapproval/routing/update/reset`,
        method: 'POST',
        data: payload,
      }),
      transformResponse: (res: {
        body: any;
        statusCode: number;
        error: {
          errorCode: number;
          errorMessage: string;
        }[];
      }) => {
        if ([200, 201].includes(res.statusCode)) return res.body;
        throw res.error[0];
      },
      invalidatesTags: ['MyApprovals', 'MyApprovalsDetails'],
    }),
    updateApprovalStatus: builder.mutation<any, UpdateApprovalStatusArgType>({
      query: ({ affiliateCode, aboNumber, payload }) => ({
        url: `/am/abo/${affiliateCode}/${aboNumber}/awardapproval/approver`,
        method: 'POST',
        data: payload,
      }),
      transformResponse: (res: {
        body: any;
        statusCode: number;
        error: {
          errorCode: number;
          errorMessage: string;
        }[];
      }) => {
        if ([200, 201].includes(res.statusCode)) return res.body;
        throw res.error[0];
      },
      invalidatesTags: ['MyApprovals', 'MyApprovalsDetails'],
    }),
  }),
});

export const {
  useGetMyAwardApprovalQuery,
  useGetAwardRoutingDetailsQuery,
  useGetUsersListQuery,
  useGetUsersListByNativeIDsQuery,
  useLazyGetUsersListByNativeIDsQuery,
  useCreateAwardRoutingMutation,
  useResetAwardRoutingMutation,
  useUpdateApprovalStatusMutation,
  useGetMyQMonthApprovalsQuery,
} = HATVAwardApprovalApi;
